import { useState, useEffect, useCallback } from "react";
import {
  _get_biker_dashboard_metrics,
  _get_biker_today_orders,
  _get_biker_my_orders,
  _biker_pickup_order,
  _biker_deliver_order,
  _biker_buyer_absent,
  _get_my_biker_details,
  _update_my_biker_details,
  _get_biker_profile,
  _update_biker_profile,
  _deactivate_biker,
  _get_available_orders,
  _accept_order,
  _get_my_orders,
  _report_buyer_absent_new,
  _biker_start_delivery,
  _biker_arrived,
  _get_biker_wallet_transactions,
} from "../../../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../common/utils/notification";

const useDeliveryData = () => {
  const [metrics, setMetrics] = useState({
    todayDeliveries: 0,
    todayEarnings: 0,
    completionRate: 0,
    activeOrders: 0,
    totalEarnings: 0,
    pendingOrders: 0,
    inTransitOrders: 0,
    deliveredToday: 0,
    returnedOrders: 0,
  });
  const [availableOrders, setAvailableOrders] = useState([]);
  const [acceptedOrders, setAcceptedOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [todayOrders, setTodayOrders] = useState([]);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [bikerProfile, setBikerProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionPagination, setTransactionPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [transactionFilters, setTransactionFilters] = useState({
    page: 1,
    limit: 10,
  });

  const [availableFilters, setAvailableFilters] = useState({
    page: 1,
    limit: 10,
  });
  const [availablePagination, setAvailablePagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const [acceptedFilters, setAcceptedFilters] = useState({
    page: 1,
    limit: 10,
    status: "active",
    search: "",
  });
  const [acceptedPagination, setAcceptedPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const [completedFilters, setCompletedFilters] = useState({
    page: 1,
    limit: 10,
    status: "delivered",
    search: "",
  });
  const [completedPagination, setCompletedPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const [todayFilters, setTodayFilters] = useState({
    page: 1,
    limit: 20,
    dateFrom: "today",
    dateTo: "today",
    search: "",
  });
  const [todayPagination, setTodayPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [previousFilters, setPreviousFilters] = useState({
    page: 1,
    limit: 20,
    dateFrom: "previous",
    dateTo: "previous",
    search: "",
  });
  const [previousPagination, setPreviousPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [metricsRes, todayOrdersRes, previousOrdersRes, profileRes] =
        await Promise.all([
          _get_biker_dashboard_metrics(),
          _get_biker_my_orders("active"),
          _get_biker_my_orders("delivered"),
          _get_my_biker_details(),
        ]);

      setMetrics(metricsRes.data.data || {});
      setTodayOrders(todayOrdersRes.data.data.orders || []);
      setTodayPagination(todayOrdersRes.data.data.pagination || {});
      setPreviousOrders(previousOrdersRes.data.data || []);
      setPreviousPagination(previousOrdersRes.data.data.pagination || {});
      setBikerProfile(profileRes.data.data || null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to load delivery data";
      setError(errorMessage);
      showErrorNotification(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const getAvailableOrders = useCallback(async () => {
    try {
      const response = await _get_available_orders(
        availableFilters.page,
        availableFilters.limit,
      );
      const data = response.data.data || { orders: [], pagination: {} };
      setAvailableOrders(data.orders || []);
      setAvailablePagination(
        data.pagination || { page: 1, limit: 10, total: 0 },
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch available orders";
      showErrorNotification(errorMessage);
    }
  }, [availableFilters]);

  const getAcceptedOrders = useCallback(async () => {
    try {
      const response = await _get_my_orders(
        acceptedFilters.page,
        acceptedFilters.limit,
        acceptedFilters.status,
        "",
        "",
        acceptedFilters.search,
      );
      const data = response.data.data || { orders: [], pagination: {} };
      setAcceptedOrders(data.orders || []);
      setAcceptedPagination(
        data.pagination || { page: 1, limit: 10, total: 0 },
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch accepted orders";
      showErrorNotification(errorMessage);
    }
  }, [acceptedFilters]);

  const getCompletedOrders = useCallback(async () => {
    try {
      const response = await _get_my_orders(
        completedFilters.page,
        completedFilters.limit,
        completedFilters.status,
        "",
        "",
        completedFilters.search,
      );
      const data = response.data.data || { orders: [], pagination: {} };
      setCompletedOrders(data.orders || []);
      setCompletedPagination(
        data.pagination || { page: 1, limit: 10, total: 0 },
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch completed orders";
      showErrorNotification(errorMessage);
    }
  }, [completedFilters]);

  const getTransactionHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await _get_biker_wallet_transactions({
        page: transactionFilters.page,
        limit: transactionFilters.limit,
      });
      const data = response.data.data || { transactions: [], pagination: {} };
      setTransactions(data.transactions || []);
      setTransactionPagination(
        data.pagination || { page: 1, limit: 10, total: 0 },
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch transaction history";
      showErrorNotification(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [transactionFilters]);

  const refreshData = useCallback(() => {
    fetchDashboardData();
    getAvailableOrders();
    getAcceptedOrders();
    getCompletedOrders();
    getTransactionHistory();
  }, [fetchDashboardData, getAvailableOrders, getAcceptedOrders, getCompletedOrders, getTransactionHistory]);

  const pickupOrder = useCallback(
    async (orderId, pickupData) => {
      try {
        setLoading(true);
        const response = await _biker_pickup_order(orderId, pickupData);
        if (response.data.status === "success") {
          showSuccessNotification("Order picked up successfully!");
          refreshData();
          return { success: true };
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to pickup order";
        showErrorNotification(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [refreshData],
  );

  const deliverOrder = useCallback(
    async (orderId, deliveryData) => {
      try {
        setLoading(true);
        await _biker_deliver_order(orderId, deliveryData);
        showSuccessNotification("Order delivered successfully!");
        refreshData();
        return { success: true };
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to deliver order";
        showErrorNotification(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [refreshData],
  );

  const reportBuyerAbsent = useCallback(
    async (orderId, absentData) => {
      try {
        setLoading(true);
        const response = await _biker_buyer_absent(orderId, absentData);
        if (response.data.status === "success") {
          showSuccessNotification("Buyer absence reported successfully!");
          refreshData();
          return { success: true };
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to report buyer absence";
        showErrorNotification(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [refreshData],
  );

  const updateBikerProfile = useCallback(async (profileData) => {
    try {
      setLoading(true);
      const response = await _update_my_biker_details(profileData);
      showSuccessNotification("Profile updated successfully!");
      fetchDashboardData();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to update profile";
      showErrorNotification(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const getBikerProfile = useCallback(async (bikerId) => {
    try {
      setLoading(true);
      const response = await _get_biker_profile(bikerId);
      return { success: true, data: response.data.data || response.data };
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch biker profile";
      showErrorNotification(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSpecificBikerProfile = useCallback(
    async (bikerId, profileData) => {
      try {
        setLoading(true);
        const response = await _update_biker_profile(bikerId, profileData);
        showSuccessNotification("Biker profile updated successfully!");
        return { success: true, data: response.data.data || response.data };
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to update biker profile";
        showErrorNotification(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const deactivateBiker = useCallback(async (bikerId) => {
    try {
      setLoading(true);
      const response = await _deactivate_biker(bikerId);
      showSuccessNotification("Biker deactivated successfully!");
      return { success: true, data: response.data.data || response.data };
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to deactivate biker";
      showErrorNotification(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);



  const acceptOrderAction = useCallback(async (orderId) => {
    try {
      setLoading(true);
      await _accept_order(orderId);
      showSuccessNotification("Order accepted successfully!");
      refreshData();
      return { success: true };
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to accept order";
      showErrorNotification(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [refreshData]);

  const reportBuyerAbsentAction = useCallback(async (deliveryId, formData) => {
    try {
      setLoading(true);
      await _report_buyer_absent_new(deliveryId, formData);
      showSuccessNotification("Buyer absence reported successfully!");
      refreshData();
      return { success: true };
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to report buyer absence";
      showErrorNotification(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const startDeliveryAction = useCallback(async (deliveryId) => {
    try {
      setLoading(true);
      await _biker_start_delivery(deliveryId);
      showSuccessNotification("Delivery started successfully!");
      refreshData();
      return { success: true };
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to start delivery";
      showErrorNotification(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const arrivedDeliveryAction = useCallback(async (deliveryId) => {
    try {
      setLoading(true);
      await _biker_arrived(deliveryId);
      showSuccessNotification("Marked as arrived successfully!");
      refreshData();
      return { success: true };
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to mark as arrived";
      showErrorNotification(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTodayFilters = useCallback((updates) => {
    setTodayFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const updatePreviousFilters = useCallback((updates) => {
    setPreviousFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateAvailableFilters = useCallback((updates) => {
    setAvailableFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateAcceptedFilters = useCallback((updates) => {
    setAcceptedFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateCompletedFilters = useCallback((updates) => {
    setCompletedFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateTransactionFilters = useCallback((updates) => {
    setTransactionFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [todayFilters, previousFilters]);

  useEffect(() => {
    getAvailableOrders();
  }, [availableFilters]);

  useEffect(() => {
    getAcceptedOrders();
  }, [acceptedFilters]);

  useEffect(() => {
    getCompletedOrders();
  }, [completedFilters]);

  useEffect(() => {
    getTransactionHistory();
  }, [transactionFilters]);

  return {
    metrics,
    todayOrders,
    previousOrders,
    bikerProfile,
    loading,
    error,
    refreshData,
    pickupOrder,
    deliverOrder,
    reportBuyerAbsent,
    updateBikerProfile,
    getBikerProfile,
    updateSpecificBikerProfile,
    deactivateBiker,
    updateTodayFilters,
    todayPagination,
    updatePreviousFilters,
    previousPagination,
    // New 3-tab structure
    availableOrders,
    acceptedOrders,
    completedOrders,
    availablePagination,
    acceptedPagination,
    completedPagination,
    updateAvailableFilters,
    updateAcceptedFilters,
    updateCompletedFilters,
    acceptOrderAction,
    reportBuyerAbsentAction,
    startDeliveryAction,
    arrivedDeliveryAction,
    // Transactions
    transactions,
    transactionPagination,
    updateTransactionFilters,
    getTransactionHistory,
  };
};

export default useDeliveryData;
